// src/pages/CrmCustomers/components/CrmCustomerList.js
import { Table, Button, Row, Col, App, Tooltip, Drawer, Typography, Flex, Grid} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSelectedCustomer, setIsEditCustomer } from "../crmCustomersSlice";
import { deleteCrmCustomer, fetchCrmCustomers } from "../crmCustomersThunks";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const { Title } = Typography;

const CrmCustomerList = ({ crmCustomers }) => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const isAdminUser = useIsAdminUser();
 const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  useEffect(() => {
    const access = getAccessType("crm_customer_entry");
    access_type_set(access);
  }, []);

  const handleDelete = async (acc_id, acc_name) => {
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete designation: ${acc_name}`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteCrmCustomer(acc_id)).unwrap();
          modal.success({ title: "Designation deleted successfully" });
          dispatch(fetchCrmCustomers());
        } catch (error) {
          modal.warning({ title: error.message || "Delete failed" });
        }
      },
    });
  };

  const columns = [
    { title: "No", render: (_, __, index) => index + 1 },
    { title: "Customer Name", dataIndex: "acc_name" },
    { title: "Profession", dataIndex: "profession" },
    { title: "Contact No", dataIndex: "contact_no" },
    { title: "Address", dataIndex: "address" },
    { title: "Area", dataIndex: "location_name" },
    { title: "Employee", dataIndex: "full_name" },
    { title: "Initial Conversation", dataIndex: "conversation" },
    { title: "Expectation %", dataIndex: "expectation_per" },
    { title: "Status", dataIndex: "status_text" },
    {
      title: "Creation Date",
      dataIndex: "created_date",
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },

    ...(access_type === "WRITE"
      ? [
          {
            title: "Actions",
            render: (_, record) => (
              <>
                <Button
                  type="link"
                  onClick={() => {
                    dispatch(setSelectedCustomer(record));
                    dispatch(setIsEditCustomer(true));
                    navigate("addNewCustomer");
                  }}
                  icon={<EditOutlined />}
                  disabled={!(isAdminUser || canEdit)}
                />
                <Button
                  type="link"
                  danger
                  onClick={() => handleDelete(record.acc_id, record.acc_name)}
                  icon={<DeleteOutlined />}
                  disabled={!(isAdminUser || canDelete)}

                />
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {/* <Row justify="end" style={{ marginBottom: 32 }}>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedCustomer(null));
                  dispatch(setIsEditCustomer(false));
                  navigate("addNewCustomer");
                }}
                data-testid="add-customer-btn"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row> */}

    <Flex
  justify="end"
  align="center"
  gap={6}
  style={{ marginBottom: 24 }}
>
  <Tooltip title="Help">
    <Button
      type="text"
      icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
      onClick={() => setHelpOpen(true)}
    />
  </Tooltip>

  {access_type == "WRITE" && (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={() => {
        dispatch(setSelectedCustomer(null));
        dispatch(setIsEditCustomer(false));
        navigate("addNewCustomer");
      }}
      data-testid="add-customer-btn"
    >
      Add New
    </Button>
  )}
</Flex>
      <Table
        rowKey="id"
        dataSource={crmCustomers}
        columns={columns}
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: crmCustomers.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
      />
      <Drawer
        title="CRM Customers Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            CRM Customer Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px",marginLeft: 17 }}>
            This section helps manage CRM customer records, contact details,
            communication history, expectations, and customer relationship tracking
            within the CRM system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Create and manage customer records</li>
              <li>Update customer contact and address information</li>
              <li>Track customer professions and areas</li>
              <li>Store initial customer conversations and notes</li>
              <li>Monitor customer expectations and follow-up status</li>
              <li>Assign customers to employees or sales staff</li>
              <li>View customer creation history and activity dates</li>
              <li>Edit or remove customer records if permitted</li>
              <li>Maintain customer relationship and communication details</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Customer records are used throughout CRM workflows.</li>
              <li>Initial conversations help track customer interest history.</li>
              <li>Expectation percentage helps measure customer conversion potential.</li>
              <li>Only authorized users can edit or delete records.</li>
              <li>Deleted customer records may affect related CRM activities.</li>
              <li>Keep customer details updated for better communication tracking.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CrmCustomerList;
